import { utils, writeFile } from 'xlsx';
import {
  DraftDanfeReportItem,
  ReportItemsSums,
} from './relatorioDraftDanfePage.types';

const headers = [
  'ncm',
  'adicao',
  'item',
  'numero_item_pu',
  'codigo',
  'quantidade',
  'descricao_dos_produtos',
  'cfop',
  'fob',
  'peso_bruto_item',
  'peso_liquido_item',
  'frete',
  'seguro',
  'acrescimo',
  'cif',
  'aliquota_ii',
  'aliquota_ipi',
  'aliquota_pis',
  'aliquota_cofins',
  'fator_icms',
  'aliquota_icms',
  'base_calculo_ii_real',
  'valor_ii_real',
  'base_calculo_ipi_real',
  'valor_ipi_real',
  'base_calculo_pis_cofins_real',
  'valor_pis_real',
  'valor_cofins_real',
  'valor_taxa_siscomex_real',
  'soma_total',
  'base_icms',
  'base_reduzida',
  'valor_icms_real',
  'valor_afrmm_real',
  'numero_danfe',
  'quantidade_volumes',
  'especie_volume',
  'transportadora',
  'di',
];

const formattedHeaders = [
  'NCM',
  'Adição',
  'Item',
  'Número Item PU',
  'Código',
  'QTDE',
  'Descrição dos Produtos',
  'CFOP',
  'FOB R$',
  'Peso Bruto Total do Item',
  'Peso Líquido Total do Item',
  'Frete R$',
  'Seguro R$',
  'Acréscimo',
  'CIF R$',
  '% II',
  '% IPI',
  '% PIS',
  '% COFINS',
  'ICMS Base',
  '% ICMS',
  'Valor Base II',
  'II',
  'Valor Base IPI',
  'IPI',
  'Valor Base PIS/COFINS',
  'PIS',
  'COFINS',
  'TAXA SISCOMEX',
  'Soma',
  'Base ICMS R$',
  'BASE Reduzida',
  'Valor do ICMS',
  'AFRMM',
  'Invoice',
  'Quantidade de Volumes',
  'Especie de Volume',
  'Transportadora',
  'DI',
];

/**
 * It takes an array of items and returns an array of arrays of items, where each sub-array contains
 * all the items with the same DANFE number
 * @param {DraftDanfeReportItem[]} items - DraftDanfeReportItem[]
 * @returns An array of arrays of DraftDanfeReportItem
 */
export const groupItemsByDanfe = (items: DraftDanfeReportItem[]) => {
  const groupedItems: { [key: number]: DraftDanfeReportItem[] } = {};
  items.forEach((item) => {
    if (!groupedItems[item.numero_danfe]) {
      groupedItems[item.numero_danfe] = [];
    }
    groupedItems[item.numero_danfe].push(item);
  });
  return Object.values(groupedItems);
};

/**
 * It sums up all the values of a given group of items
 * @param {DraftDanfeReportItem[]} group - DraftDanfeReportItem[]
 * @returns An object with the sums of the values of the items in the group.
 */
const calculateGroupSums = (group: DraftDanfeReportItem[]) => {
  const sums: ReportItemsSums = {
    quantidades: 0,
    fobs: 0,
    pesosBrutos: 0,
    pesosLiquidos: 0,
    fretes: 0,
    seguros: 0,
    acrescimos: 0,
    cifs: 0,
    valoresII: 0,
    valoresIPI: 0,
    valoresPIS: 0,
    valoresCOFINS: 0,
    valoresTaxaSiscomex: 0,
    valoresSoma: 0,
    valoresBasesICMS: 0,
    valoresICMS: 0,
    valoresAFRMM: 0,
    valoresBaseReduzida: 0,
    valoresBaseII: 0,
    valoresBaseIPI: 0,
    valoresBasePisCofins: 0,
  };
  group.forEach((item) => {
    sums.quantidades += item.quantidade;
    sums.fobs += item.fob;
    sums.pesosBrutos += item.peso_bruto_item;
    sums.pesosLiquidos += item.peso_liquido_total;
    sums.fretes += item.frete;
    sums.seguros += item.seguro;
    sums.acrescimos += item.acrescimo;
    sums.cifs += item.cif;
    sums.valoresII += item.valor_ii_real;
    sums.valoresIPI += item.valor_ipi_real;
    sums.valoresPIS += item.valor_pis_real;
    sums.valoresCOFINS += item.valor_cofins_real;
    sums.valoresTaxaSiscomex += item.valor_taxa_siscomex_real;
    sums.valoresSoma += item.soma_total;
    sums.valoresBasesICMS += item.base_icms;
    sums.valoresICMS += item.valor_icms_real;
    sums.valoresAFRMM += item.valor_afrmm_real;
    sums.valoresBaseReduzida += item.base_reduzida ? item.base_reduzida : 0;
    sums.valoresBaseII += item.base_calculo_ii_real;
    sums.valoresBaseIPI += item.base_calculo_ipi_real;
    sums.valoresBasePisCofins += item.base_calculo_pis_cofins_real;
  });
  return sums;
};

/**
 * It removes keys from the items that are not headers
 * @param {DraftDanfeReportItem} item - DraftDanfeReportItem
 * @returns A new object with only the header keys
 */
const removeNonHeaderValues = (item: DraftDanfeReportItem) => {
  const newItem = {};
  
  headers.forEach((header) => {
    if(item.hasOwnProperty(header))
      (newItem as any)[header] = (item as any)[header];
  });

  // não seria exatamente esse objeto, mas somente os campos que possuem no header
  // daria pra usar um optional ou especificar os campos, não fiz por questões de prazo
  return newItem as DraftDanfeReportItem;
};

/**
 * It takes an array of arrays of DraftDanfeReportItem objects, and generates an Excel spreadsheet with
 * each array as a separate worksheet
 * @param {DraftDanfeReportItem[][]} data - DraftDanfeReportItem[][]
 * @param {string | undefined} processo - string | undefined
 */
export const generateSpreadsheet = (
  data: DraftDanfeReportItem[][],
  processo: string | undefined
) => {
  const workbook = utils.book_new();
  data.forEach((group) => {
    const sums = calculateGroupSums(group);

    // Remove itens que não são headers do group
    const worksheetValues = group.map(removeNonHeaderValues);

    // Essa função cria a worksheet com os dados do json. 
    // Note que se um header não existir na lista de headers, 
    // ele será adicionado ao final da tabela, por isso é necessario limpar o group antes de criar
    const worksheet = utils.json_to_sheet(worksheetValues, { header: headers});

    // Add the formatted headers
    utils.sheet_add_aoa(worksheet, [formattedHeaders], {
      origin: { c: 0, r: 0 },
    });
    // Add totals row
    utils.sheet_add_aoa(
      worksheet,
      [
        [
          'TOTAL',
          '.',
          '.',
          '.',
          '.',
          sums.quantidades,
          '.',
          '.',
          sums.fobs,
          sums.pesosBrutos,
          sums.pesosLiquidos,
          sums.fretes,
          sums.seguros,
          sums.acrescimos,
          sums.cifs,
          '.',
          '.',
          '.',
          '.',
          '.',
          '.',
          sums.valoresBaseII,
          sums.valoresII,
          sums.valoresBaseIPI,
          sums.valoresIPI,
          sums.valoresBasePisCofins,
          sums.valoresPIS,
          sums.valoresCOFINS,
          sums.valoresTaxaSiscomex,
          sums.valoresSoma,
          '.',
          sums.valoresBaseReduzida,
          sums.valoresICMS,
          sums.valoresAFRMM,
          '.',
        ],
      ],
      {
        origin: -1,
      }
    );

    // Add quantidade row
    utils.sheet_add_aoa(
      worksheet,
      [['QUANTIDADE', group[0].quantidade_volumes, group[0].quantidade_volumes]],
      {
        origin: -1,
      }
    );

    // Add container row
    utils.sheet_add_aoa(worksheet, [['CONTAINER', ...(group[0]?.numero_container ?? [])]], {
      origin: -1,
    });

    // Add referencia despachante row
    utils.sheet_add_aoa(
      worksheet,
      [['REFERÊNCIA DESPACHANTE', group[0].referencia_despachante]],
      {
        origin: -1,
      }
    );

    // Add referencia cliente row
    utils.sheet_add_aoa(
      worksheet,
      [['REFERÊNCIA CLIENTE', ...(group[0]?.referencia_cliente ?? [])]],
      {
        origin: -1,
      }
    );

    // Add exportador row
    utils.sheet_add_aoa(worksheet, [['EXPORTADOR', group[0].exportador]], {
      origin: -1,
    });

    // Add DI row
    utils.sheet_add_aoa(worksheet, [['DI', group[0].di]], {
      origin: -1,
    });

    // Add frete row
    utils.sheet_add_aoa(worksheet, [['FRETE', sums.fretes]], {
      origin: -1,
    });

    // Add peso bruto row
    utils.sheet_add_aoa(worksheet, [['PESO BRUTO', sums.pesosBrutos]], {
      origin: -1,
    });

    // Add peso liquido row
    utils.sheet_add_aoa(
      worksheet,
      [['PESO LIQUIDO', group[0].peso_liquido_total]],
      {
        origin: -1,
      }
    );

    // Add SICOMEX row
    utils.sheet_add_aoa(worksheet, [['SICOMEX', sums.valoresTaxaSiscomex]], {
      origin: -1,
    });

    // Add Transportadora row
    utils.sheet_add_aoa(
      worksheet,
      [['TRANSPORTADORA', group[0].transportadora]],
      {
        origin: -1,
      }
    );

    // Add taxa SEK row
    utils.sheet_add_aoa(worksheet, [['TAXA SEK', group[0].moeda_taxa]], {
      origin: -1,
    });

    // Set A column width
    worksheet['!cols'] = [{ wch: 20 }];

    utils.book_append_sheet(
      workbook,
      worksheet,
      `Danfe ${group[0].numero_danfe}`
    );
  });

  writeFile(workbook, `Relatório Draft Danfe - ${processo}.xlsx`);
};
